import React, { forwardRef, ReactNode, useId } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import styles from './StepList.module.scss';

export type StepState = 'done' | 'next' | 'later';

export interface StepItem {
  key: string;
  state: StepState;
  title: ReactNode;
  description?: ReactNode;
  /** Przycisk albo link prowadzący do kroku; na telefonie ląduje pod tekstem. */
  action?: ReactNode;
}

export interface StepListProps {
  title: ReactNode;
  /** Np. „1 z 3 kroków”. */
  progress: ReactNode;
  steps: StepItem[];
  doneLabel: string;
  todoLabel: string;
}

/**
 * Lista kroków „co dalej” z licznikiem postępu. Sekcja przyjmuje fokus programowo (tabIndex -1),
 * żeby po zapisie czytnik ekranu ogłosił nagłówek listy.
 */
const StepList = forwardRef<HTMLElement, StepListProps>(
  ({ title, progress, steps, doneLabel, todoLabel }, ref) => {
    const headingId = useId();

    return (
      <section ref={ref} className={styles.list} aria-labelledby={headingId} tabIndex={-1}>
        <header className={styles.header}>
          <h2 id={headingId} className={styles.title}>
            {title}
          </h2>
          <span className={styles.progress}>{progress}</span>
        </header>
        <ol className={styles.steps}>
          {steps.map((step, index) => (
            <li key={step.key} className={`${styles.step} ${styles[step.state]}`}>
              <span className={styles.mark}>
                {step.state === 'done' ? <CheckOutlined aria-hidden /> : index + 1}
              </span>
              <span className={styles.text}>
                <strong className={styles.stepTitle}>
                  <span className={styles.srOnly}>
                    {step.state === 'done' ? doneLabel : todoLabel}:{' '}
                  </span>
                  {step.title}
                </strong>
                {step.description && <span className={styles.description}>{step.description}</span>}
              </span>
              {step.action}
            </li>
          ))}
        </ol>
      </section>
    );
  }
);

StepList.displayName = 'StepList';

export default StepList;
